"use client";
import { giftCardItem } from "@/utils/giftCardUtils";
import pom from "@/utils/pom";
import { applyDataTestId } from "@/utils/testUtils";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { SingleProductDescriptionFocusedPrice } from "./SingleProductDescriptionFocusedPrice";
import { SingleProductDescriptionFocusedDuration } from "./singleProductionDescriptionFocusedDuration";

export interface SingleProductProps {
  product: any;
  merchantName: string;
}

export const SingleProductDescriptionFocused = ({
  product,
  merchantName,
}: SingleProductProps) => {
  const t = useTranslations("SingleProductDescriptionFocused");
  const isGiftCardProduct = product.id === giftCardItem.id;
  const productImage =
    product.images && product.images.length > 0
      ? product.images.reduce((prev: any, current: any) => {
          if (
            current.name.includes("xxxhdpi") ||
            (current.name.includes("xxhdpi") &&
              !prev.name.includes("xxxhdpi")) ||
            (current.name.includes("xhdpi") &&
              !prev.name.includes("xxhdpi") &&
              !prev.name.includes("xxxhdpi"))
          ) {
            return current;
          }
          return prev;
        }, product.images[0])
      : null;

  const giftCardDescription = t("giftCardDescription", { merchantName });
  const fullDescription = !isGiftCardProduct
    ? product.description
    : giftCardDescription;

  return (
    <div
      key={product.id}
      data-testid={applyDataTestId(pom.products.singleProduct)}
      className="flex relative h-[120px] shadow-md rounded-b-md cursor-pointer bg-white"
    >
      <div className="w-full flex flex-col justify-between px-3 pt-4 pb-3 flex-grow min-w-0">
        <div className="flex justify-between">
          <h3 className="flex flex-col sm:flex-row overflow-hidden">
            <span
              data-testid={applyDataTestId(pom.general.itemName)}
              className="line-clamp-1 text-lg font-medium text-ellipsis"
            >
              {product.name}
            </span>
            {product.serviceItem && !isGiftCardProduct && (
              <SingleProductDescriptionFocusedDuration
                duration={product?.serviceItem?.duration}
              />
            )}
          </h3>

          <div className="m-0">
            <SingleProductDescriptionFocusedPrice
              product={product}
              isGiftCardProduct={isGiftCardProduct}
            />
          </div>
        </div>
        <p
          data-testid={applyDataTestId(pom.products.productDescription)}
          className="text-wrap line-clamp-2 text-[15px] m-0 text-ellipsis"
          title={fullDescription}
        >
          {fullDescription}
        </p>
      </div>

      {productImage && (
        <div className={`flex-shrink-0 relative w-[120px] h-[120px]`}>
          <div
            className={`absolute top-0 left-0 w-full h-full transition-opacity duration-300`}
          >
            <Image
              data-testid={applyDataTestId(pom.landingPage.productCardImage)}
              width={120}
              height={120}
              sizes="100vw"
              alt={product.imageAlt || t("productImageAlt")}
              src={productImage.source}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full object-cover"
            />
          </div>
        </div>
      )}
      {!product.available && (
        <div className="absolute h-full w-full inset-0 bg-white opacity-50 rounded-md"></div>
      )}
    </div>
  );
};
